import React, { useEffect, useMemo, useRef, useState } from "react";
import { Copy, Check, Trash2, RotateCcw, Bookmark, X, Sigma, AlertTriangle, Search, ChevronDown, ChevronRight, Image as ImageIcon, Telescope, Network } from "lucide-react";
import { BlockMath } from "react-katex";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import { useTrackPageView } from "../hooks/useAnalytics";
import { latexToUnicode } from "../lib/latexDecode";
import { unicodeToLatex } from "../lib/unicodeToLatex";
import { prettifyUnicodeFractions } from "../lib/prettyFraction";
import { toHtmlFraction } from "../lib/htmlFraction";
import { renderEquationSvg, svgToPngBlob } from "../lib/equationSvg";
import PageHeader from "../components/PageHeader";
import { useNavigate } from "react-router-dom";
import {
  getSavedEquations, saveEquation, removeEquation, renameEquation,
} from "../lib/equations";

/**
 * Equation Builder — dual-mode (Unicode OR LaTeX) with live preview, search/filter,
 * collapsible sections, fraction builder, tensor builder, and a LaTeX → Unicode decoder.
 */

// ===== UNICODE palettes ===== (paste-anywhere plain text)
// Tags are used for search/filter.
const UNICODE_PALETTES = [
  {
    name: "Greek", tags: ["greek", "letters", "physics"], color: "text-[#ffd60a]",
    symbols: [
      "α","β","γ","δ","ε","ζ","η","θ","ι","κ","λ","μ","ν","ξ",
      "ο","π","ρ","σ","ς","τ","υ","φ","χ","ψ","ω",
      "Α","Β","Γ","Δ","Ε","Ζ","Η","Θ","Ι","Κ","Λ","Μ","Ν","Ξ",
      "Ο","Π","Ρ","Σ","Τ","Υ","Φ","Χ","Ψ","Ω",
      "ϵ","ϑ","ϰ","ϕ","ϱ","ϖ","ϐ","ϒ","ƛ","ℏ","ℓ","ℵ","ℶ","ℷ","ℸ",
    ],
  },
  {
    name: "Operators & Relations", tags: ["math", "operators", "relations", "compare"], color: "text-[#00ff88]",
    symbols: [
      "+","−","×","÷","·","∗","⁄","±","∓","=","≠","≈","≡","≜",
      "≅","≃","≢","∝","<",">","≤","≥","≦","≧","≪","≫","⪅","⪆",
      "⋘","⋙","≺","≻","≼","≽","∞","∅","∂","∇","∘",
      "⊕","⊖","⊗","⊘","⊙","⊞","⊠","⊡","⨁","⨂","⨀","⨃","⨄","⨆","⨅","⩕","⩖",
    ],
  },
  {
    name: "Calculus & Set Theory", tags: ["calculus", "integral", "set"], color: "text-[#60a5fa]",
    symbols: [
      "∫","∬","∭","∮","∯","∰","∑","∏","∐","√","∛","∜",
      "ⅆ","ⅇ","ⅈ","ⅉ","𝜕","Δ","∆","∇","∇²","∇·","∇×",
      "∀","∃","∄","∈","∉","∋","∌","⊂","⊃","⊆","⊇","⊄","⊅","⊊","⊋",
      "∪","∩","∖","∁","⊍","ℕ","ℤ","ℚ","ℝ","ℂ","ℙ","ℍ","𝔽","𝕊",
    ],
  },
  {
    name: "Physics & Relativity", tags: ["physics", "relativity", "tensor", "finsler"], color: "text-[#ff9500]",
    symbols: [
      "ℏ","ℓ","ℒ","ℋ","𝓛","𝓗","Λ","∇","∂","□","◇",
      "Γ","Γᵘ","Γᵛ","Δ","Ω","Ψ","Φ","ε₀","μ₀","ϕ","ψ",
      "ρ","σ","τ","κ","λ","θ","φ","ω","χ","η","ξ","ζ",
      "γₘᵥ","gᵤᵥ","Rᵤᵥ","Tᵤᵥ","∇μ","∂μ","⟨","⟩","ℓₚ","tₚ","mₚ","ℛ","ℛᵢⱼ",
    ],
  },
  {
    name: "Geometry & Shapes", tags: ["geometry", "shapes", "angles"], color: "text-[#ff3b30]",
    symbols: [
      "∠","∡","∢","⊥","∥","∦","≅","≃","≇",
      "△","▽","◁","▷","▱","□","■","▭","○","◯","◌",
      "•","★","☆","✕","✓","✗","↦","↣","↠","⟂","∴","∵",
      "◆","◇","◈","◉","⬡","⬢","◊","◇","▲","▼","◣","◤","◥","◢",
    ],
  },
  {
    name: "Logic", tags: ["logic", "boolean", "proof"], color: "text-purple-400",
    symbols: ["¬","∧","∨","⊕","→","←","↔","⇒","⇐","⇔","⊢","⊨","⊤","⊥","∴","∵","□","◊","∎","∝","≜","⟹","⟺"],
  },
  {
    name: "Arrows", tags: ["arrows", "mapping", "directions"], color: "text-[#ff3b30]",
    symbols: [
      "←","→","↑","↓","↔","↕","⇐","⇒","⇑","⇓","⇔","⇕",
      "↖","↗","↘","↙","↩","↪","⇄","⇆","⇌","⇋","↦","↣","↠","⟶","⟵","⟷","⟹","⟸","⟺","⤴","⤵",
      "⇀","↼","⇁","↽","⇈","⇊","⇇","⇉","⤒","⤓","⤡","⤢","⟼","⟿","⤳",
    ],
  },
  {
    name: "Statistics & Accents", tags: ["statistics", "accent", "hat", "bar"], color: "text-[#60a5fa]",
    symbols: [
      "x̄","ȳ","x̂","ŷ","x̃","x⃗","x′","x″","x‴",
      "μ","σ","σ²","ρ","χ²","ℙ","𝔼","Var","Cov","∼","≈","≅","≃","∝","!","‼",
      "ẋ","ẍ","x⃗","x̃","x̌","x̆","x̊","x⃛","x̅","x⃐","x⃑",
    ],
  },
  {
    name: "Superscript (full alphabet+)", tags: ["superscript", "power", "exponent"], color: "text-zinc-300",
    // Includes ALL Unicode-available superscript Latin letters + Greek + operators
    symbols: [
      "⁰","¹","²","³","⁴","⁵","⁶","⁷","⁸","⁹","⁺","⁻","⁼","⁽","⁾",
      "ᵃ","ᵇ","ᶜ","ᵈ","ᵉ","ᶠ","ᵍ","ʰ","ⁱ","ʲ","ᵏ","ˡ","ᵐ","ⁿ","ᵒ","ᵖ","ʳ","ˢ","ᵗ","ᵘ","ᵛ","ʷ","ˣ","ʸ","ᶻ",
      "ᴬ","ᴮ","ᴰ","ᴱ","ᴳ","ᴴ","ᴵ","ᴶ","ᴷ","ᴸ","ᴹ","ᴺ","ᴼ","ᴾ","ᴿ","ᵀ","ᵁ","ⱽ","ᵂ",
      "ᵅ","ᵝ","ᵞ","ᵟ","ᵋ","ᶿ","ᶥ","ᶲ","ᵠ","ᵡ","ᵝ","ᵟ",  // Greek superscripts
      "ᵎ","ᵕ","ᵒ","ᶺ",
    ],
  },
  {
    name: "Subscript (full alphabet+)", tags: ["subscript", "index"], color: "text-zinc-300",
    // All Unicode-available subscript glyphs; many letters have no subscript form so we use what exists.
    symbols: [
      "₀","₁","₂","₃","₄","₅","₆","₇","₈","₉","₊","₋","₌","₍","₎",
      "ₐ","ₑ","ₕ","ᵢ","ⱼ","ₖ","ₗ","ₘ","ₙ","ₒ","ₚ","ᵣ","ₛ","ₜ","ᵤ","ᵥ","ₓ",
      "ᵦ","ᵧ","ᵨ","ᵩ","ᵪ",  // Greek subscripts: β γ ρ φ χ
    ],
  },
  {
    name: "Unicode Fractions & Numbers", tags: ["fraction", "numbers"], color: "text-zinc-300",
    symbols: ["½","⅓","¼","⅕","⅙","⅐","⅛","⅑","⅒","⅔","¾","⅖","⅗","⅘","⅚","⅜","⅝","⅞","↉","‰","‱","°","′","″","‴"],
  },
  {
    name: "Brackets & Misc", tags: ["brackets", "punctuation"], color: "text-zinc-300",
    symbols: ["(",")","[","]","{","}","⟨","⟩","⌈","⌉","⌊","⌋","|","‖","‹","›","«","»","′","″","‴","⁗","…","⋯","⋮","⋱","⋰","⟦","⟧","⟪","⟫"],
  },
  {
    name: "Script / Calligraphic (fancy R, fancy L)", tags: ["script", "calligraphic", "fancy"], color: "text-[#ffd60a]",
    symbols: [
      "𝒜","ℬ","𝒞","𝒟","ℰ","ℱ","𝒢","ℋ","ℐ","𝒥","𝒦","ℒ","ℳ","𝒩",
      "𝒪","𝒫","𝒬","ℛ","𝒮","𝒯","𝒰","𝒱","𝒲","𝒳","𝒴","𝒵",
      "𝒶","𝒷","𝒸","𝒹","ℯ","𝒻","ℊ","𝒽","𝒾","𝒿","𝓀","ℓ","𝓂","𝓃",
      "ℴ","𝓅","𝓆","𝓇","𝓈","𝓉","𝓊","𝓋","𝓌","𝓍","𝓎","𝓏",
    ],
  },
  {
    name: "Double-Struck / Blackboard Bold (double-lined)", tags: ["blackboard", "double", "bold", "mathbb"], color: "text-[#60a5fa]",
    symbols: [
      "𝔸","𝔹","ℂ","𝔻","𝔼","𝔽","𝔾","ℍ","𝕀","𝕁","𝕂","𝕃","𝕄","ℕ",
      "𝕆","ℙ","ℚ","ℝ","𝕊","𝕋","𝕌","𝕍","𝕎","𝕏","𝕐","ℤ",
      "𝕒","𝕓","𝕔","𝕕","𝕖","𝕗","𝕘","𝕙","𝕚","𝕛","𝕜","𝕝","𝕞","𝕟",
      "𝕠","𝕡","𝕢","𝕣","𝕤","𝕥","𝕦","𝕧","𝕨","𝕩","𝕪","𝕫",
      "𝟘","𝟙","𝟚","𝟛","𝟜","𝟝","𝟞","𝟟","𝟠","𝟡",
    ],
  },
  {
    name: "Fraktur / Gothic", tags: ["fraktur", "gothic", "old"], color: "text-[#ff9500]",
    symbols: [
      "𝔄","𝔅","ℭ","𝔇","𝔈","𝔉","𝔊","ℌ","ℑ","𝔍","𝔎","𝔏","𝔐","𝔑",
      "𝔒","𝔓","𝔔","ℜ","𝔖","𝔗","𝔘","𝔙","𝔚","𝔛","𝔜","ℨ",
      "𝔞","𝔟","𝔠","𝔡","𝔢","𝔣","𝔤","𝔥","𝔦","𝔧","𝔨","𝔩","𝔪","𝔫",
      "𝔬","𝔭","𝔮","𝔯","𝔰","𝔱","𝔲","𝔳","𝔴","𝔵","𝔶","𝔷",
    ],
  },
  {
    name: "Currency", tags: ["currency", "money"], color: "text-[#00ff88]",
    symbols: ["$","€","£","¥","¢","₽","₹","₿","₩","₪","₫","₱","₦","₣","₤","₧","₨","₭","₮","₯","₰","₲","₳","₴","₵","₶","₷","₸","₻","₼","₾","₿"],
  },
  {
    name: "Music & Performance", tags: ["music", "sound"], color: "text-purple-400",
    symbols: ["♭","♮","♯","♩","♪","♫","♬","𝄞","𝄢","𝄪","𝄫","𝅗𝅥","𝅘𝅥","𝅘𝅥𝅮","𝅘𝅥𝅯","𝅘𝅥𝅰"],
  },
  {
    name: "Combining Accents (use after a letter)", tags: ["accent", "combining", "diacritic"], color: "text-zinc-400",
    // These overlay on the preceding character. Click them AFTER a letter is typed.
    symbols: ["\u0302","\u0304","\u0305","\u0306","\u0307","\u0308","\u030A","\u030B","\u030C","\u0327","\u0328","\u20D7","\u20D6","\u0303","\u0301","\u0300","\u0311","\u20DB","\u20DC","\u0359","\u035C"],
  },
  {
    name: "Set Operators (extended)", tags: ["set", "extended"], color: "text-[#60a5fa]",
    symbols: ["⋂","⋃","⋀","⋁","⨄","⊎","⊍","⊏","⊐","⊑","⊒","⊓","⊔","⫁","⫂","⫃","⫄","⫅","⫆"],
  },
  {
    name: "Emojis — Faces & Hands", tags: ["emoji", "face", "smile", "hand", "expression"], color: "text-[#ffd60a]",
    symbols: [
      "😀","😃","😄","😁","😆","😅","🤣","😂","🙂","🙃","😉","😊","😇","🥰","😍","🤩","😘","😗","😚","😙",
      "😋","😛","😜","🤪","😝","🤑","🤗","🤭","🤫","🤔","🤐","🤨","😐","😑","😶","😏","😒","🙄","😬","🤥",
      "😌","😔","😪","🤤","😴","😷","🤒","🤕","🤢","🤮","🥵","🥶","🥴","😵","🤯","🤠","🥳","😎","🤓","🧐",
      "😕","😟","🙁","☹️","😮","😯","😲","😳","🥺","😦","😧","😨","😰","😥","😢","😭","😱","😖","😣","😞",
      "😓","😩","😫","🥱","😤","😡","😠","🤬","😈","👿","💀","☠️","👽","👻","🤖","💩",
      "👍","👎","👌","✌️","🤞","🤟","🤘","🤙","👈","👉","👆","🖕","👇","☝️","✋","🤚","🖐️","🖖","👋","🤝",
      "👏","🙌","👐","🤲","🙏","💪","🦾","🤳","🫶","💅",
    ],
  },
  {
    name: "Emojis — Hearts & Symbols", tags: ["emoji", "heart", "love", "symbol", "star"], color: "text-[#ff3b30]",
    symbols: [
      "❤️","🧡","💛","💚","💙","💜","🖤","🤍","🤎","💔","❣️","💕","💞","💓","💗","💖","💘","💝","💟","☮️",
      "✝️","☪️","🕉️","☸️","✡️","🔯","🕎","☯️","☦️","🛐","⛎","♈","♉","♊","♋","♌","♍","♎","♏","♐",
      "♑","♒","♓","🆔","⚛️","🉑","☢️","☣️","📴","📳","🈶","🈚","🈸","🈺","🈷️","✴️","🆚","💮","🉐","㊙️",
      "🌟","⭐","✨","💫","⚡","🔥","💥","☄️","☀️","🌤️","⛅","🌥️","☁️","🌦️","🌧️","⛈️","🌩️","🌨️","❄️","☃️",
      "⛄","🌬️","💨","💧","💦","☔","🌊","🌫️","🌈","🎆","🎇","🌹","🌸","🌺","🌻","🌼","🌷","💐","🌱",
    ],
  },
  {
    name: "Emojis — Tech, Tools & Science", tags: ["emoji", "tech", "tool", "science"], color: "text-[#60a5fa]",
    symbols: [
      "💻","🖥️","🖨️","⌨️","🖱️","🖲️","💽","💾","💿","📀","🧮","📱","📲","☎️","📞","📟","📠","🔋","🔌","💡",
      "🔦","🕯️","🪔","🧯","🛢️","💸","💵","💴","💶","💷","💰","💳","💎","⚖️","🦯","🧰","🔧","🔨","⚒️","🛠️",
      "⛏️","🪛","🔩","⚙️","🪤","🧲","🔫","💣","🧨","🪓","🔪","⚔️","🛡️","🚬","⚰️","🪦","⚱️","🏺","🔮",
      "📿","🧿","💈","⚗️","🔭","🔬","🕳️","💊","💉","🩸","🩹","🩺","🌡️","🧬","🧫","🧪","🔬","🧠","🫀","🫁",
    ],
  },
  {
    name: "Emojis — Animals, Food & Nature", tags: ["emoji", "animal", "food", "nature"], color: "text-[#00ff88]",
    symbols: [
      "🐶","🐱","🐭","🐹","🐰","🦊","🐻","🐼","🐨","🐯","🦁","🐮","🐷","🐸","🐵","🐔","🐧","🐦","🐤","🦆",
      "🦅","🦉","🦇","🐺","🐗","🐴","🦄","🐝","🪱","🐛","🦋","🐌","🐞","🐜","🪰","🪲","🪳","🦟","🦗","🕷️",
      "🕸️","🦂","🐢","🐍","🦎","🦖","🦕","🐙","🦑","🦐","🦞","🦀","🐡","🐠","🐟","🐬","🐳","🐋","🦈","🐊",
      "🍎","🍐","🍊","🍋","🍌","🍉","🍇","🍓","🫐","🍈","🍒","🍑","🥭","🍍","🥥","🥝","🍅","🍆","🥑","🥦",
      "🥬","🥒","🌶️","🫑","🌽","🥕","🫒","🧄","🧅","🥔","🍠","🥐","🍞","🥖","🥨","🧀","🥚","🍳","🧈","🥞",
    ],
  },
  {
    name: "Emojis — Activity, Travel & Misc", tags: ["emoji", "activity", "travel", "place"], color: "text-purple-400",
    symbols: [
      "⚽","🏀","🏈","⚾","🥎","🎾","🏐","🏉","🥏","🎱","🪀","🏓","🏸","🏒","🏑","🥍","🏏","🪃","🥅","⛳",
      "🪁","🏹","🎣","🤿","🥊","🥋","🎽","🛹","🛼","🛷","⛸️","🥌","🎿","⛷️","🏂","🪂","🏋️","🤼","🤸","⛹️",
      "🚗","🚕","🚙","🚌","🚎","🏎️","🚓","🚑","🚒","🚐","🛻","🚚","🚛","🚜","🦯","🦽","🦼","🛴","🚲","🛵",
      "🏍️","🛺","🚨","🚔","🚍","🚘","🚖","🚡","🚠","🚟","🚃","🚋","🚞","🚝","🚄","🚅","🚈","🚂","🚆","🚇",
      "✈️","🛫","🛬","🛩️","💺","🛰️","🚀","🛸","🚁","🛶","⛵","🚤","🛥️","🛳️","⛴️","🚢","⚓","🪝","⛽","🚧",
    ],
  },
];

// ===== LATEX tools ===== (live-rendered + AI-friendly when pasted into prompts)
const LATEX_PALETTES = [
  {
    name: "Structures — Fractions, Roots, Limits", tags: ["fraction", "root", "limit", "structure"], color: "text-[#ffd60a]",
    items: [
      { label: "a/b", insert: "\\frac{■}{■}", help: "Stacked fraction (top/bottom)" },
      { label: "(a/b)", insert: "\\dfrac{■}{■}", help: "Display-style stacked fraction" },
      { label: "ⁿ√x", insert: "\\sqrt[■]{■}", help: "nth root" },
      { label: "√x", insert: "\\sqrt{■}", help: "Square root" },
      { label: "xⁿ", insert: "{■}^{■}", help: "Superscript (any chars)" },
      { label: "xₙ", insert: "{■}_{■}", help: "Subscript (any chars)" },
      { label: "xᵤᵥ", insert: "{■}^{■}_{■}", help: "Sub + Super (tensor index)" },
      { label: "Σ", insert: "\\sum_{■}^{■} ■", help: "Summation with bounds" },
      { label: "Π", insert: "\\prod_{■}^{■} ■", help: "Product with bounds" },
      { label: "∫", insert: "\\int_{■}^{■} ■\\,d■", help: "Integral with bounds" },
      { label: "∬", insert: "\\iint_{■} ■\\,dA", help: "Double integral" },
      { label: "∮", insert: "\\oint_{■} ■\\,d■", help: "Closed contour integral" },
      { label: "lim", insert: "\\lim_{■ \\to ■} ■", help: "Limit" },
      { label: "vec", insert: "\\vec{■}", help: "Vector arrow" },
      { label: "hat", insert: "\\hat{■}", help: "Unit hat" },
      { label: "bar", insert: "\\bar{■}", help: "Bar (mean / conjugate)" },
      { label: "dot", insert: "\\dot{■}", help: "Time derivative" },
      { label: "ddot", insert: "\\ddot{■}", help: "Second time derivative" },
      { label: "ó", insert: "\\overline{■}", help: "Long overline" },
      { label: "binom", insert: "\\binom{■}{■}", help: "Binomial coefficient" },
      { label: "⟨…⟩", insert: "\\langle ■ \\rangle", help: "Angle brackets" },
      { label: "[matrix]", insert: "\\begin{pmatrix} ■ & ■ \\\\ ■ & ■ \\end{pmatrix}", help: "2×2 matrix" },
      { label: "‖x‖", insert: "\\lVert ■ \\rVert", help: "Norm" },
      { label: "underset", insert: "\\underset{■}{■}", help: "Item with text below" },
      { label: "overset", insert: "\\overset{■}{■}", help: "Item with text above" },
    ],
  },
  {
    name: "Greek (LaTeX)", tags: ["greek", "letters"], color: "text-[#ffd60a]",
    items: [
      ...["alpha","beta","gamma","delta","epsilon","zeta","eta","theta","iota","kappa","lambda","mu","nu","xi","pi","rho","sigma","tau","upsilon","phi","chi","psi","omega"]
        .map((n) => ({ label: `\\${n}`, insert: `\\${n} ` })),
      ...["Gamma","Delta","Theta","Lambda","Xi","Pi","Sigma","Upsilon","Phi","Psi","Omega"]
        .map((n) => ({ label: `\\${n}`, insert: `\\${n} ` })),
      { label: "\\varepsilon", insert: "\\varepsilon " },
      { label: "\\varphi", insert: "\\varphi " },
      { label: "\\vartheta", insert: "\\vartheta " },
      { label: "\\varrho", insert: "\\varrho " },
      { label: "\\hbar", insert: "\\hbar " },
      { label: "\\ell", insert: "\\ell " },
      { label: "\\partial", insert: "\\partial " },
      { label: "\\nabla", insert: "\\nabla " },
      { label: "\\infty", insert: "\\infty " },
    ],
  },
  {
    name: "Physics & Relativity — Tensors, Curvature, Gravity", tags: ["physics", "relativity", "tensor", "einstein", "finsler"], color: "text-[#ff9500]",
    items: [
      { label: "Rμν", insert: "R_{\\mu\\nu}", help: "Ricci curvature tensor" },
      { label: "R̂μν", insert: "\\hat{R}_{\\mu\\nu}", help: "Ricci tensor hat (Finsler curvature)" },
      { label: "Rᶠ", insert: "R^{F}", help: "Finsler curvature scalar" },
      { label: "R̃μνρσ", insert: "R^{\\rho}{}_{\\sigma\\mu\\nu}", help: "Riemann curvature tensor (full)" },
      { label: "R (scalar)", insert: "R\\,", help: "Ricci scalar" },
      { label: "gμν", insert: "g_{\\mu\\nu}", help: "Metric tensor" },
      { label: "g⁻¹μν", insert: "g^{\\mu\\nu}", help: "Inverse metric (raised)" },
      { label: "Γᵏμν", insert: "\\Gamma^{\\lambda}_{\\mu\\nu}", help: "Christoffel symbol of 2nd kind" },
      { label: "Tμν", insert: "T_{\\mu\\nu}", help: "Stress-energy tensor" },
      { label: "Gμν", insert: "G_{\\mu\\nu}", help: "Einstein tensor" },
      { label: "Λgμν", insert: "\\Lambda g_{\\mu\\nu}", help: "Cosmological term" },
      { label: "∂μ", insert: "\\partial_{\\mu}", help: "Partial derivative w/ index" },
      { label: "∇μ", insert: "\\nabla_{\\mu}", help: "Covariant derivative" },
      { label: "□", insert: "\\Box ", help: "d'Alembertian (wave operator)" },
      { label: "Einstein", insert: "R_{\\mu\\nu} - \\tfrac{1}{2} R\\, g_{\\mu\\nu} + \\Lambda g_{\\mu\\nu} = \\tfrac{8\\pi G}{c^{4}} T_{\\mu\\nu}", help: "Einstein field equation" },
      { label: "Geodesic", insert: "\\frac{d^{2}x^{\\mu}}{d\\tau^{2}} + \\Gamma^{\\mu}_{\\alpha\\beta} \\frac{dx^{\\alpha}}{d\\tau} \\frac{dx^{\\beta}}{d\\tau} = 0", help: "Geodesic equation" },
      { label: "ds²", insert: "ds^{2} = g_{\\mu\\nu}\\, dx^{\\mu}\\, dx^{\\nu}", help: "Line element" },
      { label: "Finsler F", insert: "F(x, y)", help: "Finsler norm / fundamental function" },
      { label: "Finsler L", insert: "L(x, y) = \\tfrac{1}{2} F^{2}(x, y)", help: "Finsler Lagrangian" },
      { label: "Finsler gᵢⱼ", insert: "g_{ij}(x, y) = \\tfrac{1}{2} \\frac{\\partial^{2} F^{2}}{\\partial y^{i} \\partial y^{j}}", help: "Finsler metric tensor" },
      { label: "Finsler Rⁱⱼ", insert: "R^{i}{}_{k} = \\ell^{j} R^{i}{}_{jkl} \\ell^{l}", help: "Finsler flag curvature" },
      { label: "Cartan tensor", insert: "C_{ijk} = \\tfrac{1}{2} \\frac{\\partial g_{ij}}{\\partial y^{k}}", help: "Cartan torsion (Finsler)" },
      { label: "Sensitivity ℛᵢⱼ", insert: "\\mathcal{R}_{ij}(x, y)", help: "Finsler Sensitivity Tensor (script R)" },
      { label: "Sensitivity ℛᵏᵢⱼ", insert: "\\mathcal{R}^{k}{}_{ij}", help: "Finsler Sensitivity Tensor — full index form" },
      { label: "Berwald Bⁱⱼₖₗ", insert: "B^{i}{}_{jkl} = -\\frac{\\partial G^{i}}{\\partial y^{j} \\partial y^{k} \\partial y^{l}}", help: "Berwald curvature (Finsler)" },
      { label: "Landsberg Lᵢⱼₖ", insert: "L_{ijk} = -\\tfrac{1}{2} F\\, y_{l} B^{l}{}_{ijk}", help: "Landsberg tensor (Finsler)" },
      { label: "fancy ℛ", insert: "\\mathcal{R}", help: "Script R (calligraphic/fancy)" },
      { label: "double-T 𝕋", insert: "\\mathbb{T}", help: "Double-struck T (blackboard bold uppercase)" },
      { label: "double-t 𝕥", insert: "\\mathbb{t}", help: "Double-struck t (blackboard bold lowercase)" },
    ],
  },
  {
    name: "Quantum & Field Theory", tags: ["quantum", "field", "qft"], color: "text-[#a78bfa]",
    items: [
      { label: "Ψ", insert: "\\Psi(\\mathbf{r}, t)", help: "Wavefunction" },
      { label: "Ĥ", insert: "\\hat{H}", help: "Hamiltonian operator" },
      { label: "⟨φ|ψ⟩", insert: "\\langle \\phi | \\psi \\rangle", help: "Inner product (bra-ket)" },
      { label: "[Â,B̂]", insert: "[\\hat{A}, \\hat{B}]", help: "Commutator" },
      { label: "{Â,B̂}", insert: "\\{\\hat{A}, \\hat{B}\\}", help: "Anticommutator" },
      { label: "Schrödinger", insert: "i\\hbar \\frac{\\partial \\Psi}{\\partial t} = \\hat{H} \\Psi", help: "Time-dependent Schrödinger equation" },
      { label: "Dirac", insert: "(i\\gamma^{\\mu} \\partial_{\\mu} - m) \\psi = 0", help: "Dirac equation" },
      { label: "Klein-Gordon", insert: "(\\Box + m^{2}) \\phi = 0", help: "Klein-Gordon equation" },
      { label: "Path integral", insert: "\\int \\mathcal{D}\\phi\\, e^{iS[\\phi]/\\hbar}", help: "Feynman path integral" },
      { label: "γμ", insert: "\\gamma^{\\mu}", help: "Dirac gamma matrix" },
      { label: "Lagrangian", insert: "\\mathcal{L} = \\tfrac{1}{2} \\partial_{\\mu} \\phi\\, \\partial^{\\mu} \\phi - V(\\phi)", help: "Scalar field Lagrangian" },
    ],
  },
  {
    name: "Relations & Operators (LaTeX)", tags: ["operators", "relations", "compare"], color: "text-[#00ff88]",
    items: [
      { label: "≤", insert: "\\le " }, { label: "≥", insert: "\\ge " },
      { label: "≠", insert: "\\ne " }, { label: "≈", insert: "\\approx " },
      { label: "≡", insert: "\\equiv " }, { label: "∝", insert: "\\propto " },
      { label: "→", insert: "\\to " }, { label: "↦", insert: "\\mapsto " },
      { label: "⇒", insert: "\\Rightarrow " }, { label: "⇔", insert: "\\Leftrightarrow " },
      { label: "∈", insert: "\\in " }, { label: "∉", insert: "\\notin " },
      { label: "⊂", insert: "\\subset " }, { label: "⊆", insert: "\\subseteq " },
      { label: "∪", insert: "\\cup " }, { label: "∩", insert: "\\cap " },
      { label: "∀", insert: "\\forall " }, { label: "∃", insert: "\\exists " },
      { label: "∅", insert: "\\emptyset " },
      { label: "⊗", insert: "\\otimes " }, { label: "⊕", insert: "\\oplus " },
      { label: "·", insert: "\\cdot " }, { label: "×", insert: "\\times " },
      { label: "±", insert: "\\pm " }, { label: "∓", insert: "\\mp " },
    ],
  },
  {
    name: "Blackboard / Number Sets", tags: ["blackboard", "sets", "mathbb"], color: "text-[#60a5fa]",
    items: [
      { label: "ℕ", insert: "\\mathbb{N}" }, { label: "ℤ", insert: "\\mathbb{Z}" },
      { label: "ℚ", insert: "\\mathbb{Q}" }, { label: "ℝ", insert: "\\mathbb{R}" },
      { label: "ℂ", insert: "\\mathbb{C}" }, { label: "ℍ", insert: "\\mathbb{H}" },
      { label: "ℙ", insert: "\\mathbb{P}" }, { label: "𝔼", insert: "\\mathbb{E}" },
      { label: "𝔽", insert: "\\mathbb{F}" }, { label: "𝕊", insert: "\\mathbb{S}" },
      { label: "𝒜", insert: "\\mathcal{A}" }, { label: "ℒ", insert: "\\mathcal{L}" },
      { label: "ℋ", insert: "\\mathcal{H}" }, { label: "ℬ", insert: "\\mathcal{B}" },
      { label: "𝒟", insert: "\\mathcal{D}" }, { label: "ℰ", insert: "\\mathcal{E}" },
    ],
  },
];

const UNICODE_QUICK = [
  { label: "x²", value: "x²" }, { label: "xⁿ", value: "xⁿ" }, { label: "x₁", value: "x₁" },
  { label: "√x", value: "√x" }, { label: "a⁄b", value: "a⁄b" },
  { label: "Σᵢ₌₁ⁿ", value: "Σᵢ₌₁ⁿ " }, { label: "∫₀^∞", value: "∫₀^∞ " },
  { label: "d/dx", value: "d⁄dx " }, { label: "∂/∂x", value: "∂⁄∂x " },
  { label: "lim x→∞", value: "lim x→∞ " }, { label: "∀x ∈ ℝ", value: "∀x ∈ ℝ " },
  { label: "± √(b²−4ac)", value: "± √(b²−4ac)" },
];

export default function EquationBuilderPage() {
  useTrackPageView("/equation");
  const nav = useNavigate();
  const [text, setText] = useState("");
  // Default to Unicode flavor — most users want paste-anywhere plain text first; LaTeX is for power users.
  const [mode, setMode] = useState("unicode");
  const [copied, setCopied] = useState(false);
  const [savedList, setSavedList] = useState(() => getSavedEquations());
  const [justSaved, setJustSaved] = useState(false);
  const [search, setSearch] = useState("");
  const [openSections, setOpenSections] = useState({}); // section name → bool
  const taRef = useRef(null);
  const historyRef = useRef([]);
  // Ref on the rendered LaTeX preview box so we can snapshot it to a PNG for "Copy as Image".
  const latexRenderRef = useRef(null);
  const [copiedImage, setCopiedImage] = useState(false);

  useEffect(() => {
    const h = () => setSavedList(getSavedEquations());
    window.addEventListener("equations-changed", h);
    return () => window.removeEventListener("equations-changed", h);
  }, []);

  const pushHistory = (val) => {
    historyRef.current.push(val);
    if (historyRef.current.length > 50) historyRef.current.shift();
  };

  const insertAtCursor = (sym) => {
    pushHistory(text);
    const el = taRef.current;
    const start = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? text.length;
    const next = text.slice(0, start) + sym + text.slice(end);
    setText(next);
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      const firstSlot = sym.indexOf("■");
      if (firstSlot >= 0) {
        const slotStart = start + firstSlot;
        el.setSelectionRange(slotStart, slotStart + 1);
      } else {
        const pos = start + sym.length;
        el.setSelectionRange(pos, pos);
      }
    });
  };

  const onKeyDown = (e) => {
    if (e.key === "Tab") {
      const el = e.target;
      const caret = el.selectionEnd;
      const next = text.indexOf("■", caret);
      if (next >= 0) { e.preventDefault(); el.setSelectionRange(next, next + 1); }
    }
  };

  const onChange = (e) => { pushHistory(text); setText(e.target.value); };
  // Dual-format clipboard writer. For Unicode output we put BOTH the plain-text Unicode form
  // (paste-anywhere) AND an HTML form with <sup>/<sub> fraction blocks. Rich-text targets
  // (Gmail, Slack, Discord, Word, Notion, comments) honor the HTML and render a TRUE stacked
  // fraction; plain-text targets get the Unicode form with ⁄ FRACTION SLASH.
  // For LaTeX output we just write plain text — LaTeX rendering depends on the receiving app.
  const writeClipboard = async (plain, html) => {
    if (!plain) return false;
    try {
      if (html && navigator.clipboard && window.ClipboardItem) {
        const item = new ClipboardItem({
          "text/plain": new Blob([plain], { type: "text/plain" }),
          "text/html": new Blob([html], { type: "text/html" }),
        });
        await navigator.clipboard.write([item]);
        return true;
      }
      await navigator.clipboard.writeText(plain);
      return true;
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = plain; document.body.appendChild(ta); ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        return ok;
      } catch { return false; }
    }
  };
  // Primary copy. Both flavors are SYMMETRIC: always emit the chosen flavor,
  // regardless of how the workspace was authored.
  //   • Unicode mode → write Unicode plain + HTML (paste-anywhere stacked fractions).
  //   • LaTeX mode   → write LaTeX source. If the workspace currently holds Unicode
  //                    (user built via the Unicode palette and flipped to LaTeX), run
  //                    `unicodeToLatex` first so the clipboard always contains valid LaTeX.
  const onCopy = async () => {
    if (!text) return;
    if (mode === "unicode") {
      const plain = unicodePreviewSrc;
      if (!plain) return;
      const html = `<span style="font-family:monospace">${toHtmlFraction(plain)}</span>`;
      const ok = await writeClipboard(plain, html);
      if (ok) { setCopied(true); setTimeout(() => setCopied(false), 1500); }
    } else {
      const stripped = text.replace(/■/g, "");
      // If the workspace already looks like LaTeX, copy clean; otherwise
      // re-encode the Unicode into LaTeX source before copying.
      const out = looksLikeLatex ? stripped : unicodeToLatex(stripped);
      if (!out) return;
      const ok = await writeClipboard(out, null);
      if (ok) { setCopied(true); setTimeout(() => setCopied(false), 1500); }
    }
  };
  // Secondary: copy the OTHER flavor.
  const [copiedAlt, setCopiedAlt] = useState(false);
  const onCopyAlt = async () => {
    if (!text) return;
    if (mode === "latex") {
      // LaTeX mode → also write Unicode plain + HTML stacked for rich-text contexts.
      const plain = unicodePreviewSrc;
      if (!plain) return;
      const html = `<span style="font-family:monospace">${toHtmlFraction(plain)}</span>`;
      const ok = await writeClipboard(plain, html);
      if (ok) { setCopiedAlt(true); setTimeout(() => setCopiedAlt(false), 1500); toast.success("Copied as Unicode"); }
    } else {
      // Unicode mode → encode to LaTeX source.
      const out = latexPreviewSrc;
      if (!out) return;
      const ok = await writeClipboard(out, null);
      if (ok) { setCopiedAlt(true); setTimeout(() => setCopiedAlt(false), 1500); toast.success("Copied as LaTeX"); }
    }
  };
  // Copy as Image — render via our own SVG layout (clean, crisp, small file) and
  // rasterize to PNG. ALSO bundles plain text + HTML fraction in the same
  // clipboard write so paste targets pick whichever they support:
  //   • Image-capable apps (Gmail body, Word, Notion, iMessage, Twitter, Discord, Slack) → PNG
  //   • Rich-text-only apps → HTML stacked fraction
  //   • Plain-text-only apps (terminal, code editor, SMS) → Unicode `X⁄Y`
  // Single button, one click, works everywhere.
  const onCopyImage = async () => {
    if (!text) return;
    const stripped = text.replace(/■/g, "");
    // For the IMAGE: use forImage:true source so tensors keep `_(...)` markers
    // and the SVG renderer draws true subscript/superscript. Then prettify
    // fractions so `(a)⁄(b)` becomes a stacked bar.
    const imageSrc = prettifyUnicodeFractions(
      looksLikeLatex ? latexToUnicode(stripped, { forImage: true }) : stripped
    ).trim();
    // For the TEXT clipboard: use the clean inline Unicode (no brackets).
    const plain = (unicodePreviewSrc || stripped).trim();
    if (!imageSrc) return;

    let blob;
    try {
      const { svg, width, height } = renderEquationSvg(imageSrc, {
        fontSize: 28,           // small but legible — fits inside a text bubble
        padding: 14,
        color: "#000000",
        bg: "#ffffff",
      });
      blob = await svgToPngBlob(svg, width, height, 3); // 3x for retina sharpness
    } catch (e) {
      toast.error(`Image render failed: ${e?.message || "unknown"}`);
      return;
    }
    if (!blob) { toast.error("Image encoding failed"); return; }
    // INTENTIONALLY NO text/html flavor here. Many paste targets (iMessage,
    // some email clients, Notion text fields) pick text/html OVER image/png
    // when both are present — and a stripped-down version of our stacked
    // fraction HTML degrades into "numerator on line 1, denominator on line 2"
    // with no bar between them. Keeping just image/png (for image-aware apps)
    // plus text/plain (for plain-text-only apps) is far more reliable.
    let clipboardOk = false;
    if (navigator.clipboard && window.ClipboardItem) {
      try {
        await navigator.clipboard.write([
          new ClipboardItem({
            "image/png": blob,
            "text/plain": new Blob([plain], { type: "text/plain" }),
          }),
        ]);
        clipboardOk = true;
      } catch {
        // Some browsers reject multi-MIME bundles. Retry image-only.
        try {
          await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
          clipboardOk = true;
        } catch { clipboardOk = false; }
      }
    }
    if (clipboardOk) {
      setCopiedImage(true);
      setTimeout(() => setCopiedImage(false), 1500);
      toast.success("Copied — paste anywhere as an image");
    } else {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "equation.png";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setCopiedImage(true);
      setTimeout(() => setCopiedImage(false), 1500);
      toast.success("Image downloaded — your browser blocked image clipboard");
    }
  };

  const onUndo = () => { const prev = historyRef.current.pop(); if (prev !== undefined) setText(prev); };
  const onClear = () => { pushHistory(text); setText(""); taRef.current?.focus(); };
  const onSave = () => {
    const t = text.replace(/■/g, "").trim();
    if (!t) return;
    const note = window.prompt("Label this equation (optional):", "") || "";
    saveEquation(t, note.trim());
    setJustSaved(true); setTimeout(() => setJustSaved(false), 1500);
    toast.success("Equation saved");
  };
  const onLoad = (eq) => { pushHistory(text); setText(eq.text); taRef.current?.focus(); };
  // Hand the current workspace off to Deep Dive as the topic. Strips the
  // builder's caret marker (■), trims, and routes — DeepDivePage already
  // honors ?topic=… and the new ?mode=… params, so this also threads through
  // a chosen mode if one is provided. We always send `standard` because the
  // user can flip to ELI5 / Social once they land if they want.
  const onSendToDeepDive = () => {
    const t = text.replace(/■/g, "").trim();
    if (!t) {
      toast.error("Workspace is empty");
      return;
    }
    // Cap to a sane length — equations can blow up with LaTeX expansion.
    const topic = t.slice(0, 280);
    nav(`/deep-dive?topic=${encodeURIComponent(topic)}`);
    toast.success("Sent to Deep Dive — pick a mode and hit search");
  };
  // Hand the current workspace off to Connect-the-Dots. Splits the workspace
  // on common separators so each isolated term becomes its own subject chip.
  // Falls back to the whole text as a single subject if no separators exist.
  const onSendToConnectDots = () => {
    const t = text.replace(/■/g, "").trim();
    if (!t) {
      toast.error("Workspace is empty");
      return;
    }
    // Split on commas / semicolons / "vs" / "and" — the most natural ways
    // a user might type rival subjects into the workspace.
    const parts = t
      .split(/\s*(?:,|;|\s+vs\.?\s+|\s+and\s+)\s*/i)
      .map((p) => p.trim())
      .filter((p) => p && p.length <= 80);
    const subjects = parts.length >= 2 ? parts.slice(0, 12) : [t.slice(0, 280)];
    const qs = new URLSearchParams();
    qs.set("subjects", subjects.join("|"));
    nav(`/connect-dots?${qs.toString()}`);
    toast.success(`Sent to Connect the Dots — ${subjects.length} subject${subjects.length === 1 ? "" : "s"}`);
  };
  const onCopyOne = async (val) => { try { await navigator.clipboard.writeText(val); toast.success("Copied"); } catch { toast.error("Copy failed"); } };
  const onRename = (id, current) => { const next = window.prompt("Rename this equation:", current || ""); if (next === null) return; renameEquation(id, next.trim()); };

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
        if (historyRef.current.length > 0) { e.preventDefault(); onUndo(); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-compute BOTH preview sources so the two preview boxes always show the right thing.
  // latexPreviewSrc → for KaTeX: if user typed Unicode (no backslashes), re-encode via
  //   unicodeToLatex so KaTeX gets a renderable input. Empty slot markers (■) become \square.
  // unicodePreviewSrc → for the visual pretty renderer: if user typed LaTeX, run
  //   latexToUnicode so the box shows paste-anywhere Unicode visualized as stacked fractions.
  const looksLikeLatex = useMemo(
    () => /\\[a-zA-Z]+|\^\{|_\{|\\frac|\\sqrt|\\sum|\\int|\\lim|\\begin\{/.test(text),
    [text],
  );
  const latexPreviewSrc = useMemo(() => {
    const stripped = text.replace(/■/g, "\\square ");
    return looksLikeLatex ? stripped : unicodeToLatex(text);
  }, [text, looksLikeLatex]);
  const unicodePreviewSrc = useMemo(() => {
    const stripped = text.replace(/■/g, "");
    const decoded = looksLikeLatex ? latexToUnicode(stripped) : stripped;
    return prettifyUnicodeFractions(decoded);
  }, [text, looksLikeLatex]);

  const toggleSection = (name) => setOpenSections((p) => ({ ...p, [name]: !p[name] }));

  // Search/filter logic — applies to whichever palette set is active. Matches symbol contents OR
  // section name OR tags. If search is non-empty, ALL sections auto-expand to show hits.
  const palettes = mode === "unicode" ? UNICODE_PALETTES : LATEX_PALETTES;
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return palettes;
    return palettes
      .map((p) => {
        const nameMatch = p.name.toLowerCase().includes(q);
        const tagMatch = (p.tags || []).some((t) => t.toLowerCase().includes(q));
        if (mode === "unicode") {
          const symbols = p.symbols.filter((s) => s.includes(q) || s.toLowerCase().includes(q));
          if (nameMatch || tagMatch) return p;
          if (symbols.length) return { ...p, symbols };
          return null;
        } else {
          const items = (p.items || []).filter((it) =>
            it.label.toLowerCase().includes(q)
            || it.insert.toLowerCase().includes(q)
            || (it.help || "").toLowerCase().includes(q)
          );
          if (nameMatch || tagMatch) return p;
          if (items.length) return { ...p, items };
          return null;
        }
      })
      .filter(Boolean);
  }, [palettes, search, mode]);

  // When searching, auto-open every visible section so results are immediately visible.
  const isOpen = (name) => !!search.trim() || !!openSections[name];

  return (
    <div data-testid="equation-page">
      <PageHeader
        color="#ffd60a"
        label="Equation Builder"
        icon={<Sigma size={11} />}
        title={<>Build <span className="text-[#ffd60a]">Clean</span> Math.</>}
        subtitle={<>A scientist&apos;s keyboard for the rest of us. Pick <strong className="text-[#ffd60a]">Unicode</strong> for paste-anywhere plain text or <strong className="text-[#ffd60a]">LaTeX</strong> for stacked math. Built-in fraction maker, tensor builder, 1000+ symbols, and one-click bridges to Deep Dive and Connect the Dots.</>}
        testid="equation-header"
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10">

      {/* Mode toggle */}
      <div className="flex items-center gap-2 mb-4 flex-wrap border border-white/15 bg-[#0a0a0a] px-3 py-2">
        <span className="font-mono text-[10px] tracking-[0.25em] text-zinc-500">// PALETTE FLAVOR:</span>
        <div className="inline-flex border border-white/15" role="tablist">
          {[
            { id: "unicode", label: "Unicode (paste-anywhere)" },
            { id: "latex", label: "LaTeX (stacked + AI-ready)" },
          ].map((m) => (
            <button key={m.id} type="button" onClick={() => setMode(m.id)} data-testid={`mode-${m.id}`}
              className={`px-3 py-1.5 font-mono text-[10px] tracking-[0.18em] uppercase transition-colors ${
                mode === m.id ? "bg-[#ffd60a] text-[#050505]" : "text-zinc-400 hover:text-white"
              }`}>
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {/* Workspace — textarea on top, action buttons wrap underneath so they
          never end up floating awkwardly when the equation gets long. */}
      <div className="mb-4">
        <textarea
          ref={taRef} value={text} onChange={onChange} onKeyDown={onKeyDown}
          data-testid="equation-textarea"
          placeholder={mode === "latex"
            ? "Paste LaTeX to auto-decode it (see the green panel below), or click any LaTeX tool to build. ■ marks an editable slot — Tab to jump."
            : "Type, or click any Unicode symbol below. Use the Fraction Builder for stacked-line fractions."}
          className="w-full min-h-[140px] bg-[#0a0a0a] border border-white/15 focus:border-[#ffd60a] outline-none p-5 font-mono text-2xl text-white leading-relaxed resize-y transition-colors placeholder:text-sm placeholder:text-zinc-600 placeholder:tracking-normal placeholder:font-normal"
        />
        <div className="flex flex-wrap gap-2 mt-3">
          <button type="button" onClick={onCopy} disabled={!text} data-testid="equation-copy-btn"
            className={`inline-flex items-center justify-center gap-2 min-w-[170px] px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] transition-colors disabled:opacity-70 ${
              copied
                ? "bg-[#00ff88] text-[#050505]"
                : mode === "latex"
                  ? "bg-[#ffd60a] text-[#050505] hover:bg-white"
                  : "bg-[#00ff88] text-[#050505] hover:bg-white"
            }`}>
            {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : (mode === "latex" ? "Copy LaTeX" : "Copy Unicode")}
          </button>
          <button type="button" onClick={onCopyAlt} disabled={!text}
            data-testid={mode === "latex" ? "equation-copy-unicode-btn" : "equation-copy-latex-btn"}
            title={mode === "latex" ? "Decode the LaTeX into paste-anywhere Unicode and copy" : "Encode the Unicode into LaTeX source and copy"}
            className={`inline-flex items-center justify-center gap-2 min-w-[170px] px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] transition-colors disabled:opacity-70 ${
              copiedAlt
                ? "bg-[#00ff88] text-[#050505]"
                : mode === "latex"
                  ? "bg-[#00ff88] text-[#050505] hover:bg-white"
                  : "bg-[#ffd60a] text-[#050505] hover:bg-white"
            }`}>
            {copiedAlt ? <Check size={14} /> : <Copy size={14} />} {copiedAlt ? "Copied" : (mode === "latex" ? "Copy → Unicode" : "Copy → LaTeX")}
          </button>
          <button type="button" onClick={onCopyImage} disabled={!text} data-testid="equation-copy-image-btn"
            title="Render the equation as an image (PNG) and copy it. Paste anywhere — Gmail, iMessage, Word, Notion — as a true stacked fraction."
            className={`inline-flex items-center justify-center gap-2 min-w-[170px] px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] transition-colors disabled:opacity-70 ${
              copiedImage ? "bg-[#00ff88] text-[#050505]" : "bg-[#a78bfa] text-[#050505] hover:bg-white"
            }`}>
            {copiedImage ? <Check size={14} /> : <ImageIcon size={14} />}
            {copiedImage ? "Copied" : "Copy Fraction"}
            {!copiedImage && (
              <span className="inline-flex flex-col items-center leading-none ml-1" aria-hidden="true">
                <span className="text-[10px] leading-none">a</span>
                <span className="block h-px bg-current w-3 my-0.5" />
                <span className="text-[10px] leading-none">b</span>
              </span>
            )}
          </button>
          <button type="button" onClick={onSave} disabled={!text.trim()} data-testid="equation-save-btn"
            className={`inline-flex items-center justify-center gap-2 min-w-[170px] px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] transition-colors disabled:opacity-70 ${
              justSaved ? "bg-[#00ff88] text-[#050505]" : "bg-[#050505] border border-purple-500/50 text-purple-300 hover:bg-purple-500/10"
            }`}>
            {justSaved ? <Check size={14} /> : <Bookmark size={14} />} {justSaved ? "Saved" : "Save"}
          </button>
          <button type="button" onClick={onSendToDeepDive} disabled={!text.trim()} data-testid="equation-send-deep-dive-btn"
            title="Send the current workspace to Deep Dive as a research topic"
            className="inline-flex items-center justify-center gap-2 min-w-[170px] px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] bg-[#050505] border border-[#22d3ee]/50 text-[#22d3ee] hover:bg-[#22d3ee]/10 transition-colors disabled:opacity-70">
            <Telescope size={14} /> Deep Dive
          </button>
          <button type="button" onClick={onSendToConnectDots} disabled={!text.trim()} data-testid="equation-send-connect-dots-btn"
            title="Send the current workspace to Connect the Dots — splits on commas / vs / and into separate subjects"
            className="inline-flex items-center justify-center gap-2 min-w-[170px] px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] bg-[#050505] border border-[#f472b6]/50 text-[#f472b6] hover:bg-[#f472b6]/10 transition-colors disabled:opacity-70">
            <Network size={14} /> Connect Dots
          </button>
          <button type="button" onClick={onUndo} data-testid="equation-undo-btn"
            disabled={historyRef.current.length === 0}
            className="inline-flex items-center justify-center gap-2 min-w-[170px] px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] bg-[#050505] border border-white/15 text-zinc-400 hover:text-[#ffd60a] hover:border-[#ffd60a]/50 transition-colors disabled:opacity-70">
            <RotateCcw size={14} /> Undo
          </button>
          <button type="button" onClick={onClear} data-testid="equation-clear-btn" disabled={!text}
            className="inline-flex items-center justify-center gap-2 min-w-[170px] px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] bg-[#050505] border border-white/15 text-zinc-400 hover:text-[#ff3b30] hover:border-[#ff3b30]/50 transition-colors disabled:opacity-70">
            <Trash2 size={14} /> Clear
          </button>
        </div>
      </div>

      {/* TWO LIVE PREVIEWS — always visible side-by-side.
            • LaTeX box  → shows the LaTeX SOURCE CODE form of the workspace
              (unicodeToLatex re-encodes Unicode → LaTeX so you can copy / read it).
            • Unicode box → shows the visual paste-anywhere Unicode form
              (latexToUnicode + CSS-stacked U+2044 fractions). */}
      <div className="grid lg:grid-cols-2 gap-3 mb-4">
        {/* LaTeX source preview */}
        <div className="border border-[#ffd60a]/30 bg-[#0a0a0a]" data-testid="equation-preview-latex">
          <div className="px-4 py-2 border-b border-[#ffd60a]/30 font-mono text-[10px] tracking-[0.25em] text-[#ffd60a] flex items-center justify-between">
            <span>// LIVE PREVIEW</span>
            <span>LATEX (SOURCE)</span>
          </div>
          <div className="px-6 py-6 min-h-[120px] flex items-center justify-center text-white text-lg overflow-x-auto">
            {!text ? (
              <span className="font-mono text-xs text-zinc-600 tracking-[0.2em]">EMPTY</span>
            ) : (
              <pre className="font-mono text-lg text-[#ffd60a] whitespace-pre-wrap break-words text-center w-full">{latexPreviewSrc}</pre>
            )}
          </div>
        </div>
        {/* Unicode visual preview */}
        <div className="border border-[#00ff88]/30 bg-[#0a0a0a]" data-testid="equation-preview-unicode">
          <div className="px-4 py-2 border-b border-[#00ff88]/30 font-mono text-[10px] tracking-[0.25em] text-[#00ff88] flex items-center justify-between">
            <span>// LIVE PREVIEW</span>
            <span>UNICODE (PASTE-ANYWHERE)</span>
          </div>
          <div className="px-6 py-6 min-h-[120px] flex items-center justify-center text-white text-2xl overflow-x-auto">
            {!text ? (
              <span className="font-mono text-xs text-zinc-600 tracking-[0.2em]">EMPTY</span>
            ) : (
              <UnicodePrettyPreview text={unicodePreviewSrc} />
            )}
          </div>
        </div>
      </div>

      {/* OFFSCREEN render target for "Copy as Image" — actual KaTeX render on a clean white
          background, large enough to look sharp. html2canvas snapshots this DOM into a PNG
          that gets written to the clipboard as image/png. */}
      <div
        ref={latexRenderRef}
        aria-hidden="true"
        style={{
          position: "absolute", left: "-9999px", top: 0,
          background: "#ffffff", color: "#000000",
          padding: "32px 48px", fontSize: "32px",
        }}
      >
        {text ? <KatexBoundary src={latexPreviewSrc} /> : null}
      </div>

      {/* Unicode-mode auto-convert helper — only shows when LaTeX-shaped tokens are detected
          (otherwise it'd just echo the input back at the user). */}
      {mode === "unicode" && looksLikeLatex && (
        <UnicodeAutoConvert source={text} onApply={(unicode) => {
          pushHistory(text);
          setText(unicode);
          toast.success("Converted to Unicode");
        }} />
      )}

      {/* Saved */}
      {savedList.length > 0 && (
        <section className="mb-12 border border-white/10 bg-[#0a0a0a]" data-testid="saved-equations">
          <div className="px-4 py-3 border-b border-white/10 font-mono text-[10px] tracking-[0.25em] text-purple-400 flex items-center gap-2">
            <Bookmark size={11} /> SAVED EQUATIONS — {savedList.length}
          </div>
          <ul className="px-4 py-3 space-y-2">
            {savedList.map((eq) => (
              <li key={eq.id} data-testid={`saved-eq-${eq.id}`}
                className="group flex items-center gap-3 border border-white/10 bg-[#0F0F0F] hover:bg-[#141414] hover:border-purple-500/40 transition-colors p-3">
                <button type="button" onClick={() => onLoad(eq)} className="flex-1 text-left min-w-0" title="Load into the builder">
                  <div className="font-mono text-lg text-white truncate">{eq.text}</div>
                  {eq.note && <div className="font-mono text-[10px] tracking-[0.15em] text-zinc-500 mt-1 truncate">// {eq.note}</div>}
                </button>
                <button type="button" onClick={() => onCopyOne(eq.text)} className="px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#ffd60a] border border-[#ffd60a]/30 hover:bg-[#ffd60a] hover:text-[#050505] transition-colors" title="Copy">
                  <Copy size={12} />
                </button>
                <button type="button" onClick={() => onRename(eq.id, eq.note)} className="px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 border border-white/10 hover:text-white hover:border-white/30 transition-colors" title="Rename">✎</button>
                <button type="button" onClick={() => removeEquation(eq.id)} className="px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-[#ff3b30] transition-colors" title="Delete">
                  <X size={12} />
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Builders — Fraction in both modes (UX: shouldn't disappear when switching flavors).
          Both builders are now mode-aware: they emit LaTeX in LaTeX mode, Unicode in Unicode mode. */}
      <UnicodeFractionBuilder onInsert={insertAtCursor} mode={mode} />
      <TensorBuilder onInsert={insertAtCursor} mode={mode} />

      {mode === "unicode" && (
        <section className="mb-12 border border-white/10 bg-[#0a0a0a]">
          <div className="px-4 py-3 border-b border-white/10 font-mono text-[10px] tracking-[0.25em] text-zinc-400">// QUICK UNICODE RECIPES</div>
          <div className="px-4 py-3 flex flex-wrap gap-2">
            {UNICODE_QUICK.map((q) => (
              <button key={q.label} type="button" onClick={() => insertAtCursor(q.value)}
                data-testid={`quick-${q.label}`}
                className="px-3 py-2 bg-[#0F0F0F] border border-white/10 hover:border-[#ffd60a]/60 hover:bg-[#141414] font-mono text-base text-white transition-colors">
                {q.label}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Search bar + section index */}
      <div className="-mx-4 sm:-mx-8 px-4 sm:px-8 py-3 bg-[#050505] border-y border-white/10 mb-6">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-1 min-w-[220px]">
            <Search size={14} className="text-[#ffd60a]" />
            <input
              type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              data-testid="palette-search"
              placeholder={`Search ${mode === "unicode" ? "symbols, categories, tags" : "tools, equations, tags"}…`}
              className="flex-1 bg-transparent outline-none font-mono text-xs text-white placeholder:text-zinc-600"
            />
            {search && (
              <button type="button" onClick={() => setSearch("")} className="text-zinc-500 hover:text-white" aria-label="Clear search">
                <X size={14} />
              </button>
            )}
          </div>
          <button type="button" onClick={() => {
            const all = {}; for (const p of palettes) all[p.name] = true; setOpenSections(all);
          }} className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 hover:text-white px-2 py-1">
            Expand all
          </button>
          <button type="button" onClick={() => setOpenSections({})}
            className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 hover:text-white px-2 py-1">
            Collapse all
          </button>
        </div>
        {!search && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="category-jump">
            {filtered.map((p) => (
              <button key={p.name} type="button" onClick={() => {
                toggleSection(p.name);
                requestAnimationFrame(() => {
                  document.getElementById(`palette-anchor-${p.name}`)?.scrollIntoView({ block: "start", behavior: "smooth" });
                });
              }}
                className="px-2 py-1 font-mono text-[9px] tracking-[0.18em] uppercase border border-white/10 text-zinc-400 hover:text-[#ffd60a] hover:border-[#ffd60a]/40 transition-colors">
                {p.name.split(" — ")[0].split(" (")[0]}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Filtered palette list — collapsible by default */}
      <div className="space-y-3">
        {filtered.length === 0 && search && (
          <div className="font-mono text-xs tracking-[0.2em] text-zinc-500 py-8 text-center">
            // NO MATCHES FOR "{search}" — TRY DIFFERENT TERMS OR{" "}
            <button onClick={() => setSearch("")} className="text-[#ffd60a] underline">CLEAR</button>
          </div>
        )}
        {filtered.map((p) => {
          const count = mode === "unicode" ? p.symbols.length : p.items.length;
          const open = isOpen(p.name);
          return (
            <section key={p.name} id={`palette-anchor-${p.name}`}
              data-testid={`palette-${(mode === "latex" ? "latex-" : "") + p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="border border-white/10 bg-[#0a0a0a]">
              <button type="button" onClick={() => toggleSection(p.name)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-white/[0.02] transition-colors text-left">
                <span className={`font-mono text-[10px] tracking-[0.25em] uppercase ${p.color}`}>
                  // {p.name} <span className="text-zinc-500">— {count}</span>
                </span>
                {open ? <ChevronDown size={14} className="text-zinc-500" /> : <ChevronRight size={14} className="text-zinc-500" />}
              </button>
              {open && (
                <div className="px-4 pb-4 pt-1">
                  {mode === "unicode" ? (
                    <div className="grid grid-cols-8 sm:grid-cols-12 lg:grid-cols-[repeat(16,minmax(0,1fr))] gap-1.5">
                      {p.symbols.map((s, i) => (
                        <button key={`${p.name}-${i}-${s}`} type="button" onClick={() => insertAtCursor(s)}
                          className="aspect-square flex items-center justify-center bg-[#0F0F0F] border border-white/10 hover:border-white/40 hover:bg-[#141414] text-white text-lg sm:text-xl transition-colors"
                          aria-label={`Insert ${s}`} title={s}>
                          {s}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-1.5">
                      {p.items.map((it, i) => (
                        <button key={`${p.name}-${i}`} type="button" onClick={() => insertAtCursor(it.insert)}
                          className="text-left p-2.5 bg-[#0F0F0F] border border-white/10 hover:border-[#ffd60a]/60 hover:bg-[#141414] transition-colors group"
                          title={it.help || it.insert}>
                          <div className="font-heading text-white text-base leading-tight truncate">{it.label}</div>
                          <div className="font-mono text-[9px] tracking-[0.1em] text-zinc-500 group-hover:text-[#ffd60a] truncate mt-0.5">
                            {it.help || it.insert}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </section>
          );
        })}
      </div>

      <div className="mt-16 border border-white/10 bg-[#0a0a0a] p-5 font-mono text-[10px] tracking-[0.25em] text-zinc-500 leading-relaxed">
        // TIP — <span className="text-[#ffd60a]">Unicode mode</span> pastes anywhere chat / comments / email accept plain text. <span className="text-[#ffd60a]">LaTeX mode</span> renders real stacked math AND is the format every modern AI understands. Paste <span className="text-white">R<sub>μν</sub></span> straight into your next Deep Dive prompt and the analyst will read it like normal math, no escaping required. Save your favorites; the saved library follows you on this browser.
      </div>
      </div>
    </div>
  );
}

// ===== Unicode-mode helper: when user accidentally has LaTeX in the workspace =====
function UnicodeAutoConvert({ source, onApply }) {
  const decoded = useMemo(() => {
    try { return latexToUnicode(source.replace(/■/g, "")); } catch { return ""; }
  }, [source]);
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    if (!decoded) return;
    try { await navigator.clipboard.writeText(decoded); setCopied(true); setTimeout(() => setCopied(false), 1500); toast.success("Unicode copied"); }
    catch { toast.error("Copy failed"); }
  };
  if (!decoded) return null;
  return (
    <div className="mb-10 border border-[#00ff88]/30 bg-[#0a0a0a] p-4" data-testid="unicode-auto-convert">
      <div className="font-mono text-[10px] tracking-[0.25em] text-[#00ff88] mb-2 flex items-center gap-2">
        <AlertTriangle size={11} /> // LATEX DETECTED IN UNICODE MODE — DECODED VERSION BELOW
      </div>
      <div className="font-mono text-xl text-white mb-3 break-words">{decoded}</div>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => onApply(decoded)} data-testid="auto-convert-apply"
          className="px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] bg-[#00ff88] text-[#050505] hover:bg-white transition-colors">
          Replace workspace with Unicode
        </button>
        <button type="button" onClick={onCopy} data-testid="auto-convert-copy"
          className={`inline-flex items-center gap-1.5 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
            copied ? "bg-[#00ff88] text-[#050505]" : "border border-[#00ff88]/40 text-[#00ff88] hover:bg-[#00ff88] hover:text-[#050505]"
          }`}>
          {copied ? <Check size={11} /> : <Copy size={11} />} {copied ? "Copied" : "Copy Unicode"}
        </button>
      </div>
    </div>
  );
}

// ===== Unicode visual renderer =====
// Takes Unicode text (paste-anywhere) and visualizes it like a calculator display:
//   • Splits on U+2044 (FRACTION SLASH) and renders each X⁄Y pair as a CSS-stacked fraction
//     (top above a horizontal rule, bottom below). The underlying TEXT in the workspace stays
//     pure Unicode — only the preview is laid out visually.
//   • Everything else passes through as plain mono text. Native super/subscript glyphs (xⁿ, x₁)
//     already render correctly, and stacked Unicode fractions (½ ⅔ etc.) are single glyphs.
// Tokenizer: walk the string, when we hit U+2044 we extend backward to the previous whitespace
// (or string start) for the numerator and forward to the next whitespace (or end) for the
// denominator. This matches what the Fraction Builder emits AND ad-hoc typed `a⁄b`.
function UnicodePrettyPreview({ text }) {
  const parts = useMemo(() => {
    const out = [];
    const s = text;
    let i = 0;
    const isBreak = (c) => !c || /[\s+\-=<>,;]/.test(c);
    // Scan backward from `end` (exclusive) for a numerator boundary.
    // Recognizes balanced ()-wrapped groups so `(a+b)⁄(c-d)` stacks correctly,
    // else falls back to "back to last whitespace / operator" run.
    const scanNumStart = (end) => {
      if (end <= 0) return 0;
      if (s[end - 1] === ")") {
        let depth = 1;
        for (let k = end - 2; k >= 0; k--) {
          if (s[k] === ")") depth++;
          else if (s[k] === "(") { depth--; if (depth === 0) return k; }
        }
      }
      let k = end;
      while (k > 0 && !isBreak(s[k - 1])) k--;
      return k;
    };
    const scanDenEnd = (start) => {
      if (start >= s.length) return s.length;
      if (s[start] === "(") {
        let depth = 1;
        for (let k = start + 1; k < s.length; k++) {
          if (s[k] === "(") depth++;
          else if (s[k] === ")") { depth--; if (depth === 0) return k + 1; }
        }
      }
      let k = start;
      while (k < s.length && !isBreak(s[k])) k++;
      return k;
    };
    // Strip a single layer of wrapping parens from `(...)` → `...` for display only.
    const unwrap = (str) => {
      if (str.length >= 2 && str[0] === "(" && str[str.length - 1] === ")") {
        // Check the parens are balanced (only one pair at the outermost level)
        let depth = 0;
        for (let k = 0; k < str.length; k++) {
          if (str[k] === "(") depth++;
          else if (str[k] === ")") {
            depth--;
            if (depth === 0 && k < str.length - 1) return str;
          }
        }
        return str.slice(1, -1);
      }
      return str;
    };
    while (i < s.length) {
      const idx = s.indexOf("\u2044", i);
      if (idx === -1) { out.push({ t: "txt", v: s.slice(i) }); break; }
      const nStart = scanNumStart(idx);
      const dEnd = scanDenEnd(idx + 1);
      if (nStart > i) out.push({ t: "txt", v: s.slice(i, nStart) });
      const num = s.slice(nStart, idx);
      const den = s.slice(idx + 1, dEnd);
      if (num && den) out.push({ t: "frac", n: unwrap(num), d: unwrap(den) });
      else out.push({ t: "txt", v: s.slice(nStart, dEnd) });
      i = dEnd;
    }
    return out;
  }, [text]);

  return (
    <div className="font-mono text-3xl text-white whitespace-pre-wrap text-center inline-flex flex-wrap items-center justify-center gap-x-0.5">
      {parts.map((p, idx) =>
        p.t === "frac" ? (
          <span key={idx} className="inline-flex flex-col items-center align-middle leading-none mx-1" data-testid="frac-stack">
            <span className="px-2 pb-0.5">{p.n}</span>
            <span className="block h-px bg-white w-full" />
            <span className="px-2 pt-0.5">{p.d}</span>
          </span>
        ) : (
          <span key={idx}>{p.v}</span>
        )
      )}
    </div>
  );
}


function KatexBoundary({ src }) {
  try { return <BlockMath math={src} />; }
  catch {
    return (
      <div className="font-mono text-xs text-[#ff3b30] flex items-center gap-2">
        <AlertTriangle size={14} /> LaTeX parse error — keep typing or hit Undo
      </div>
    );
  }
}

// ===== LaTeX → Unicode decoder panel =====
function UnicodeDecoder({ source }) {
  const decoded = useMemo(() => {
    try { return latexToUnicode(source.replace(/■/g, "")); } catch { return ""; }
  }, [source]);
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    if (!decoded) return;
    try { await navigator.clipboard.writeText(decoded); setCopied(true); setTimeout(() => setCopied(false), 1500); toast.success("Unicode copied"); }
    catch { toast.error("Copy failed"); }
  };
  return (
    <div className="mb-10 border border-[#00ff88]/20 bg-[#0a0a0a]" data-testid="equation-decoded">
      <div className="px-4 py-2 border-b border-[#00ff88]/20 font-mono text-[10px] tracking-[0.25em] text-[#00ff88] flex items-center justify-between gap-3">
        <span className="flex items-center gap-2"><Sigma size={11} /> // DECODED → UNICODE (PASTE-ANYWHERE)</span>
        <button type="button" onClick={onCopy} disabled={!decoded} data-testid="decoder-copy-btn"
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors disabled:opacity-70 ${
            copied ? "bg-[#00ff88] text-[#050505]" : "border border-[#00ff88]/40 text-[#00ff88] hover:bg-[#00ff88] hover:text-[#050505]"
          }`}>
          {copied ? <Check size={11} /> : <Copy size={11} />} {copied ? "Copied" : "Copy Unicode"}
        </button>
      </div>
      <div className="px-6 py-4 text-white font-mono text-xl leading-snug break-words overflow-x-auto">
        {decoded || <span className="text-zinc-600 text-xs tracking-[0.2em]">(empty)</span>}
      </div>
    </div>
  );
}

// ===== Unicode Fraction Builder =====
function UnicodeFractionBuilder({ onInsert, mode }) {
  const [num, setNum] = useState("");
  const [den, setDen] = useState("");
  const [copied, setCopied] = useState(false);
  const numRef = useRef(null);
  const previewTex = useMemo(() => {
    if (!num && !den) return "";
    const escape = (s) => s.replace(/\\/g, "\\backslash ").replace(/\{/g, "\\{").replace(/\}/g, "\\}");
    return `\\dfrac{\\text{${escape(num) || "?"}}}{\\text{${escape(den) || "?"}}}`;
  }, [num, den]);
  const buildOutput = () => {
    const n = num.trim() || "?"; const d = den.trim() || "?";
    // LaTeX flavor: emit a real \dfrac{} block so it renders stacked in the preview / pastes into KaTeX-aware contexts.
    if (mode === "latex") return `\\dfrac{${n}}{${d}}`;
    // Unicode flavor: prefer pre-baked single glyphs for simple integers, else use the fraction-slash U+2044.
    const PRETTY = { "1/2":"½","1/3":"⅓","1/4":"¼","1/5":"⅕","1/6":"⅙","1/7":"⅐","1/8":"⅛","1/9":"⅑","1/10":"⅒","2/3":"⅔","3/4":"¾","2/5":"⅖","3/5":"⅗","4/5":"⅘","5/6":"⅚","3/8":"⅜","5/8":"⅝","7/8":"⅞" };
    return PRETTY[`${n}/${d}`] || `${n}⁄${d}`;
  };
  const handleInsert = () => {
    onInsert(buildOutput());
    setNum(""); setDen("");
    requestAnimationFrame(() => numRef.current?.focus());
    toast.success("Fraction inserted");
  };
  const handleCopy = async () => {
    const out = buildOutput();
    try {
      await navigator.clipboard.writeText(out);
      setCopied(true); setTimeout(() => setCopied(false), 1500);
      toast.success("Fraction copied");
    } catch { toast.error("Copy failed"); }
  };
  const hasContent = !!(num.trim() || den.trim());
  return (
    <section className="mb-10 border border-[#ffd60a]/30 bg-[#0a0a0a]" data-testid="unicode-fraction-builder">
      <div className="px-4 py-2 border-b border-[#ffd60a]/30 font-mono text-[10px] tracking-[0.25em] text-[#ffd60a] flex items-center gap-2">
        <Sigma size={11} /> // FRACTION BUILDER — TYPE TOP &amp; BOTTOM · {mode === "latex" ? <span className="text-white"><code>\dfrac{"{num}{den}"}</code></span> : <span className="text-white"><code>a⁄b</code></span>}
      </div>
      <div className="grid lg:grid-cols-[1fr_auto_1fr_auto] gap-4 items-center p-4">
        <div>
          <label className="block font-mono text-[9px] tracking-[0.2em] text-zinc-500 mb-1">NUMERATOR (TOP)</label>
          <input ref={numRef} value={num} onChange={(e) => setNum(e.target.value)} data-testid="frac-numerator"
            placeholder="e.g. ∂Ψ or a+b"
            className="w-full bg-[#0a0a0a] border border-white/15 focus:border-[#ffd60a] outline-none px-3 py-2 font-mono text-lg text-white transition-colors" />
          <div className="my-1 mx-auto h-px bg-[#ffd60a] w-[80%]" />
          <input value={den} onChange={(e) => setDen(e.target.value)} data-testid="frac-denominator"
            placeholder="e.g. ∂t or c²"
            className="w-full bg-[#0a0a0a] border border-white/15 focus:border-[#ffd60a] outline-none px-3 py-2 font-mono text-lg text-white transition-colors" />
          <div className="mt-2 font-mono text-[9px] tracking-[0.2em] text-zinc-500">DENOMINATOR (BOTTOM)</div>
        </div>
        <div className="hidden lg:flex items-center justify-center text-zinc-500 font-mono">→</div>
        <div className="bg-[#0a0a0a] border border-white/10 px-4 py-4 min-h-[100px] flex items-center justify-center">
          {previewTex ? <KatexBoundary src={previewTex} /> : <span className="font-mono text-[10px] tracking-[0.2em] text-zinc-600">PREVIEW</span>}
        </div>
        <div className="flex flex-col gap-2">
          <button type="button" onClick={handleInsert} disabled={!hasContent} data-testid="frac-insert-btn"
            className="inline-flex items-center justify-center gap-2 px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] bg-[#ffd60a] text-[#050505] hover:bg-white transition-colors disabled:opacity-70 disabled:cursor-not-allowed">
            Insert {mode === "latex" ? "\\dfrac" : "⁄"}
          </button>
          <button type="button" onClick={handleCopy} disabled={!hasContent} data-testid="frac-copy-btn"
            className={`inline-flex items-center justify-center gap-2 px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] transition-colors disabled:opacity-70 disabled:cursor-not-allowed ${
              copied ? "bg-[#00ff88] text-[#050505]" : "border border-[#ffd60a]/40 text-[#ffd60a] hover:bg-[#ffd60a] hover:text-[#050505]"
            }`}>
            {copied ? <Check size={12} /> : <Copy size={12} />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
      </div>
    </section>
  );
}

// ===== Tensor Builder =====
// Maps used to show preview characters in the style dropdown labels.
const STYLE_MAP_BB = { A:"𝔸",B:"𝔹",C:"ℂ",D:"𝔻",E:"𝔼",F:"𝔽",G:"𝔾",H:"ℍ",I:"𝕀",J:"𝕁",K:"𝕂",L:"𝕃",M:"𝕄",N:"ℕ",O:"𝕆",P:"ℙ",Q:"ℚ",R:"ℝ",S:"𝕊",T:"𝕋",U:"𝕌",V:"𝕍",W:"𝕎",X:"𝕏",Y:"𝕐",Z:"ℤ",a:"𝕒",b:"𝕓",c:"𝕔",d:"𝕕",e:"𝕖",f:"𝕗",g:"𝕘",h:"𝕙",i:"𝕚",j:"𝕛",k:"𝕜",l:"𝕝",m:"𝕞",n:"𝕟",o:"𝕠",p:"𝕡",q:"𝕢",r:"𝕣",s:"𝕤",t:"𝕥",u:"𝕦",v:"𝕧",w:"𝕨",x:"𝕩",y:"𝕪",z:"𝕫" };
const STYLE_MAP_CAL = { A:"𝒜",B:"ℬ",C:"𝒞",D:"𝒟",E:"ℰ",F:"ℱ",G:"𝒢",H:"ℋ",I:"ℐ",J:"𝒥",K:"𝒦",L:"ℒ",M:"ℳ",N:"𝒩",O:"𝒪",P:"𝒫",Q:"𝒬",R:"ℛ",S:"𝒮",T:"𝒯",U:"𝒰",V:"𝒱",W:"𝒲",X:"𝒳",Y:"𝒴",Z:"𝒵",a:"𝒶",b:"𝒷",c:"𝒸",d:"𝒹",e:"ℯ",f:"𝒻",g:"ℊ",h:"𝒽",i:"𝒾",j:"𝒿",k:"𝓀",l:"ℓ",m:"𝓂",n:"𝓃",o:"ℴ",p:"𝓅",q:"𝓆",r:"𝓇",s:"𝓈",t:"𝓉",u:"𝓊",v:"𝓋",w:"𝓌",x:"𝓍",y:"𝓎",z:"𝓏" };
const STYLE_MAP_FRAK = { A:"𝔄",B:"𝔅",C:"ℭ",D:"𝔇",E:"𝔈",F:"𝔉",G:"𝔊",H:"ℌ",I:"ℑ",J:"𝔍",K:"𝔎",L:"𝔏",M:"𝔐",N:"𝔑",O:"𝔒",P:"𝔓",Q:"𝔔",R:"ℜ",S:"𝔖",T:"𝔗",U:"𝔘",V:"𝔙",W:"𝔚",X:"𝔛",Y:"𝔜",Z:"ℨ",a:"𝔞",b:"𝔟",c:"𝔠",d:"𝔡",e:"𝔢",f:"𝔣",g:"𝔤",h:"𝔥",i:"𝔦",j:"𝔧",k:"𝔨",l:"𝔩",m:"𝔪",n:"𝔫",o:"𝔬",p:"𝔭",q:"𝔮",r:"𝔯",s:"𝔰",t:"𝔱",u:"𝔲",v:"𝔳",w:"𝔴",x:"𝔵",y:"𝔶",z:"𝔷" };

function styledChar(base, style) {
  const ch = (base || "X")[0];
  const map = style === "mathbb" ? STYLE_MAP_BB : style === "mathcal" ? STYLE_MAP_CAL : style === "mathfrak" ? STYLE_MAP_FRAK : null;
  return map ? (map[ch] || ch) : ch;
}

// Pick base letter, upper indices, lower indices → outputs both LaTeX AND a Unicode approximation.
// Lets the user one-click custom tensor notation instead of typing 5-10 chars every time.
function TensorBuilder({ onInsert, mode }) {
  // Defaults intentionally use ASCII indices (i,j) — they have real Unicode
  // subscript glyphs (ᵢⱼ) so the "Insert Unicode" preview shows a proper tensor
  // (Rᵢⱼ) instead of a flat string (Rμν) where μ/ν have no plain-text subscript
  // form. Switch the workspace to LaTeX mode if you want Greek indices.
  const [base, setBase] = useState("R");
  const [upper, setUpper] = useState("");
  const [lower, setLower] = useState("ij");
  const [style, setStyle] = useState("plain"); // plain | hat | mathcal | mathbb | mathfrak

  const latex = useMemo(() => {
    // Idiomatic LaTeX: only wrap multi-char indices in braces. Single-character
    // base / indices stay un-braced so the copied source renders cleanly
    // wherever it's pasted (KaTeX, Overleaf, MathJax, GitHub markdown, etc.).
    let b = base || "X";
    if (style === "hat") b = `\\hat{${b}}`;
    else if (style === "mathcal") b = `\\mathcal{${b}}`;
    else if (style === "mathbb") b = `\\mathbb{${b}}`;
    else if (style === "mathfrak") b = `\\mathfrak{${b}}`;
    const wrap = (s) => (s.length > 1 ? `{${s}}` : s);
    if (upper && lower) return `${b}^${wrap(upper)}_${wrap(lower)}`;
    if (upper) return `${b}^${wrap(upper)}`;
    if (lower) return `${b}_${wrap(lower)}`;
    return b;
  }, [base, upper, lower, style]);

  // Dedicated tensor → Unicode formatter. Builds the styled base FIRST (using STYLE_MAP_*) then
  // attaches indices using true Unicode super/subscripts when every char has a glyph, else falls
  // back to `^(...)` / `_(...)` notation. Avoids the literal-brace bug you'd get from passing
  // `{R}_{μν}` through the generic decoder.
  const SUP_TBL = { "0":"⁰","1":"¹","2":"²","3":"³","4":"⁴","5":"⁵","6":"⁶","7":"⁷","8":"⁸","9":"⁹","+":"⁺","-":"⁻","−":"⁻","=":"⁼","(":"⁽",")":"⁾","a":"ᵃ","b":"ᵇ","c":"ᶜ","d":"ᵈ","e":"ᵉ","f":"ᶠ","g":"ᵍ","h":"ʰ","i":"ⁱ","j":"ʲ","k":"ᵏ","l":"ˡ","m":"ᵐ","n":"ⁿ","o":"ᵒ","p":"ᵖ","r":"ʳ","s":"ˢ","t":"ᵗ","u":"ᵘ","v":"ᵛ","w":"ʷ","x":"ˣ","y":"ʸ","z":"ᶻ","α":"ᵅ","β":"ᵝ","γ":"ᵞ","δ":"ᵟ","ε":"ᵋ","θ":"ᶿ","ι":"ᶥ","φ":"ᵠ","χ":"ᵡ" };
  const SUB_TBL = { "0":"₀","1":"₁","2":"₂","3":"₃","4":"₄","5":"₅","6":"₆","7":"₇","8":"₈","9":"₉","+":"₊","-":"₋","−":"₋","=":"₌","(":"₍",")":"₎","a":"ₐ","e":"ₑ","h":"ₕ","i":"ᵢ","j":"ⱼ","k":"ₖ","l":"ₗ","m":"ₘ","n":"ₙ","o":"ₒ","p":"ₚ","r":"ᵣ","s":"ₛ","t":"ₜ","u":"ᵤ","v":"ᵥ","x":"ₓ","β":"ᵦ","γ":"ᵧ","ρ":"ᵨ","φ":"ᵩ","χ":"ᵪ" };

  const unicode = useMemo(() => {
    const ch = (base || "X")[0];
    const styledBase = (
      style === "mathbb" ? (STYLE_MAP_BB[ch] || ch)
      : style === "mathcal" ? (STYLE_MAP_CAL[ch] || ch)
      : style === "mathfrak" ? (STYLE_MAP_FRAK[ch] || ch)
      : style === "hat" ? `${ch}\u0302`  // combining circumflex
      : ch
    );
    // Per-char mapping: subscript what we can, leave the rest at full size.
    // Unicode doesn't have subscript glyphs for μ ν α σ τ etc., so for those
    // indices we just concat them bare (same as typing them from the symbol
    // panel manually). Indices that DO have glyphs (digits, ASCII a-z minus
    // a few, and Greek β γ ρ φ χ for subs / α β γ δ ε θ ι φ χ for sups) get
    // subscripted/superscripted properly.
    const runMixed = (s, table) => {
      let out = "";
      for (const c of s) out += (table[c] ?? c);
      return out;
    };
    const supPart = upper ? runMixed(upper, SUP_TBL) : "";
    const subPart = lower ? runMixed(lower, SUB_TBL) : "";
    return styledBase + supPart + subPart;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, upper, lower, style]);

  // Flag chars in the indices that have no Unicode super/subscript glyph so the
  // UI can hint that LaTeX mode renders Greek indices like μ ν as true subscripts.
  const unsubable = useMemo(() => {
    const missing = [];
    for (const c of upper) if (!Object.prototype.hasOwnProperty.call(SUP_TBL, c)) missing.push(c);
    for (const c of lower) if (!Object.prototype.hasOwnProperty.call(SUB_TBL, c)) missing.push(c);
    return Array.from(new Set(missing));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upper, lower]);

  const onInsertLatex = () => { onInsert(latex); toast.success("Tensor inserted (LaTeX)"); };
  const onInsertUnicode = () => { onInsert(unicode); toast.success("Tensor inserted (Unicode)"); };
  const [copiedLatex, setCopiedLatex] = useState(false);
  const [copiedUnicode, setCopiedUnicode] = useState(false);
  // Clipboard fallback — some browsers / iframes block navigator.clipboard.
  // Try the modern API first, then drop to a hidden textarea + execCommand.
  const safeCopy = async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch { /* fall through */ }
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch { return false; }
  };
  const onCopyLatex = async () => {
    const ok = await safeCopy(latex);
    if (ok) { setCopiedLatex(true); setTimeout(() => setCopiedLatex(false), 1500); toast.success("LaTeX copied"); }
    else { toast.error("Copy failed"); }
  };
  const onCopyUnicode = async () => {
    const ok = await safeCopy(unicode);
    if (ok) { setCopiedUnicode(true); setTimeout(() => setCopiedUnicode(false), 1500); toast.success("Unicode copied"); }
    else { toast.error("Copy failed"); }
  };

  return (
    <section className="mb-10 border border-[#a78bfa]/30 bg-[#0a0a0a]" data-testid="tensor-builder">
      <div className="px-4 py-2 border-b border-[#a78bfa]/30 font-mono text-[10px] tracking-[0.25em] text-[#a78bfa] flex items-center gap-2">
        <Sigma size={11} /> // TENSOR BUILDER — PICK A LETTER, ADD INDICES, INSERT
      </div>
      <div className="grid lg:grid-cols-[auto_auto_auto_auto_1fr_auto] gap-3 items-end p-4">
        <div>
          <label className="block font-mono text-[9px] tracking-[0.2em] text-zinc-500 mb-1">BASE</label>
          <input value={base} onChange={(e) => setBase(e.target.value)} maxLength={4} data-testid="tensor-base"
            className="w-20 bg-[#0a0a0a] border border-white/15 focus:border-[#a78bfa] outline-none px-3 py-2 font-mono text-lg text-white transition-colors" />
        </div>
        <div>
          <label className="block font-mono text-[9px] tracking-[0.2em] text-zinc-500 mb-1">STYLE</label>
          <select value={style} onChange={(e) => setStyle(e.target.value)} data-testid="tensor-style"
            className="bg-[#0a0a0a] border border-white/15 focus:border-[#a78bfa] outline-none px-2 py-2 font-mono text-xs text-white transition-colors">
            <option value="plain">plain {base || "X"}</option>
            <option value="hat">hat {(base || "X")}̂</option>
            <option value="mathcal">script {styledChar(base, "mathcal")}</option>
            <option value="mathbb">double {styledChar(base, "mathbb")}</option>
            <option value="mathfrak">fraktur {styledChar(base, "mathfrak")}</option>
          </select>
        </div>
        <div>
          <label className="block font-mono text-[9px] tracking-[0.2em] text-zinc-500 mb-1">UPPER ⁱⁿᵈ</label>
          <input value={upper} onChange={(e) => setUpper(e.target.value)} data-testid="tensor-upper"
            placeholder="e.g. μ"
            className="w-24 bg-[#0a0a0a] border border-white/15 focus:border-[#a78bfa] outline-none px-3 py-2 font-mono text-lg text-white transition-colors" />
        </div>
        <div>
          <label className="block font-mono text-[9px] tracking-[0.2em] text-zinc-500 mb-1">LOWER ᵢₙ𝒹</label>
          <input value={lower} onChange={(e) => setLower(e.target.value)} data-testid="tensor-lower"
            placeholder="e.g. μν"
            className="w-24 bg-[#0a0a0a] border border-white/15 focus:border-[#a78bfa] outline-none px-3 py-2 font-mono text-lg text-white transition-colors" />
        </div>
        <div className="bg-[#0a0a0a] border border-white/10 px-3 py-2 min-h-[60px] flex items-center justify-center">
          <KatexBoundary src={latex} />
        </div>
        <div className="flex flex-col gap-1">
          {mode === "latex" ? (
            <>
              <button type="button" onClick={onInsertLatex} data-testid="tensor-insert-latex"
                className="px-3 py-2 font-mono text-[10px] tracking-[0.2em] uppercase bg-[#a78bfa] text-[#050505] hover:bg-white transition-colors">
                Insert LaTeX
              </button>
              <button type="button" onClick={onCopyLatex} data-testid="tensor-copy-latex"
                className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 font-mono text-[10px] tracking-[0.2em] uppercase transition-colors ${
                  copiedLatex ? "bg-[#00ff88] text-[#050505]" : "border border-[#a78bfa]/40 text-[#a78bfa] hover:bg-[#a78bfa] hover:text-[#050505]"
                }`}>
                {copiedLatex ? <Check size={11} /> : <Copy size={11} />}
                {copiedLatex ? "Copied" : "Copy"}
              </button>
            </>
          ) : (
            <>
              <button type="button" onClick={onInsertUnicode} data-testid="tensor-insert-unicode"
                className="px-3 py-2 font-mono text-[10px] tracking-[0.2em] uppercase bg-[#a78bfa] text-[#050505] hover:bg-white transition-colors">
                Insert {unicode}
              </button>
              <button type="button" onClick={onCopyUnicode} data-testid="tensor-copy-unicode"
                className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 font-mono text-[10px] tracking-[0.2em] uppercase transition-colors ${
                  copiedUnicode ? "bg-[#00ff88] text-[#050505]" : "border border-[#a78bfa]/40 text-[#a78bfa] hover:bg-[#a78bfa] hover:text-[#050505]"
                }`}>
                {copiedUnicode ? <Check size={11} /> : <Copy size={11} />}
                {copiedUnicode ? "Copied" : "Copy"}
              </button>
            </>
          )}
        </div>
      </div>
      <div className="px-4 pb-3 grid grid-cols-2 gap-2 font-mono text-[10px] text-zinc-500">
        <div><span className="text-[#a78bfa]">LaTeX:</span> <span className="text-white">{latex}</span></div>
        <div><span className="text-[#a78bfa]">Unicode:</span> <span className="text-white">{unicode}</span></div>
      </div>
      {unsubable.length > 0 && mode !== "latex" ? (
        <div data-testid="tensor-unicode-hint" className="px-4 pb-3 font-mono text-[10px] text-[#ffd60a]/80 leading-relaxed">
          <span className="text-[#ffd60a]">⚠ UNICODE LIMIT:</span> {unsubable.join(" ")} {unsubable.length === 1 ? "has" : "have"} no plain-text sub/superscript glyph in Unicode &mdash; rendered at full size. Switch the workspace to <strong className="text-white">LaTeX</strong> mode to get true subscripted Greek indices in the source preview.
        </div>
      ) : null}
    </section>
  );
}
